/**
 * カレンダー関連の定数定義
 */

import type { TimeSlot } from '../types';

// 予約ステータス: カレンダーに表示するステータスのセット
const CALENDAR_VISIBLE_STATUS_CODES: string[] = [
  'booked',
  'done',
  'in_use',
  'mail_ready',
  'received',
  'pending',
  'offer',
  'request',
];

export const DISPLAY_STATUSES = new Set(CALENDAR_VISIBLE_STATUS_CODES);

// ステータス表示名（日本語）
export const STATUS_LABELS = {
  booked: '予約確定',
  done: '貸出済み',
  in_use: '貸出中',
  received: '受信済み',
  pending: '確認中',
  cancelled: 'キャンセル済み',
  nonreserved: '予約に至らなかった',
  full: '予約がいっぱい',
  offer: 'オファー中',
  mail_ready: '要連絡/未確定'
} as const;

export const STATUS_GROUPS = {
  active: ['received', 'mail_ready', 'pending', 'booked', 'in_use'] as string[],
  completed: ['done'] as string[],
  cancelled: ['cancelled', 'nonreserved'] as string[],
  unavailable: ['full'] as string[],
} as const;

// 時間スロット定義
export const TIME_SLOTS: TimeSlot[] = [
  { type: 'bucket', id: 'morning', label: '早朝', hours: [6, 7, 8] },
  ...Array.from({ length: 13 }).map((_, i) => ({
    type: 'hour' as const,
    hour: 9 + i,
    label: `${String(9 + i).padStart(2, '0')}:00`
  })),
  { type: 'bucket', id: 'night', label: '深夜', hours: [22, 23] }
];

// Undo/Redo履歴の最大保持数
export const MAX_HISTORY = 10;

// 初期表示レンジ設定
export const INITIAL_PAST_DAYS = 7; // 今日から過去にさかのぼる日数（1週間）
export const INITIAL_FUTURE_DAYS = 365; // 今日から先に含める日数（1年）
export const INITIAL_VISIBLE_DAYS = INITIAL_PAST_DAYS + INITIAL_FUTURE_DAYS + 1; // 今日を含めた合計日数

// 無限スクロール用の読み込み閾値とチャンク
export const FUTURE_LOAD_LOOKAHEAD_DAYS = 30; // 未来側の残りが1ヶ月になったら追加ロード
export const FUTURE_LOAD_CHUNK_DAYS = 60; // 一度に読み込む未来側日数（約2ヶ月）
export const PAST_LOAD_CHUNK_DAYS = 30; // 過去側の読み込みチャンク

// 初期表示での余裕を持たせるための過去側プリフェッチ日数
export const INITIAL_PAST_PREFETCH_DAYS = PAST_LOAD_CHUNK_DAYS;
export const INITIAL_PAST_FETCH_DAYS = INITIAL_PAST_DAYS + INITIAL_PAST_PREFETCH_DAYS;

// デフォルトのカレンダー表示日数
export const DEFAULT_CALENDAR_DAYS = INITIAL_VISIBLE_DAYS;

// デフォルトのトースト表示時間（ミリ秒）
export const DEFAULT_TOAST_DURATION = 5000;

// サイトオリジン（sys系PHP呼び出し用）
export const SITE_ORIGIN = (() => {
  const envValue = process.env.REACT_APP_SITE_ORIGIN;
  if (typeof envValue === 'string' && envValue.trim() !== '') {
    return envValue.trim().replace(/\/+$/, '');
  }
  if (typeof window !== 'undefined' && (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1')) {
    return '';
  }
  return 'https://n-car.jp';
})();

// sys系APIベースパス
// SYS_API_BASE: bulk_send, mail_templates, payment/ 等 → booking.trailernchu.co.jp に配置
//   本番: 同一ドメインなので相対パス、localhost: setupProxy.js でプロキシ
export const SYS_API_BASE = '/sys/api';
// SYS_INVOICE_BASE: square_create_invoice 等 → n-car.jp に配置
export const SYS_INVOICE_BASE = `${SITE_ORIGIN}/sys/invoice`;

// API エンドポイント
export const API_BASE_URL = (() => {
  const envValue = process.env.REACT_APP_API_URL;
  if (typeof envValue === 'string' && envValue.trim() !== '') {
    return envValue.trim().replace(/\/+$/, '');
  }
  return '/dispatch-api';
})();

export const API_ENDPOINTS = {
  vehicles: `${API_BASE_URL}/vehicles`,
  vehicleTypes: `${API_BASE_URL}/vehicle-types`,
  reservations: `${API_BASE_URL}/reservations`,
  resources: `${API_BASE_URL}/resources`,
  histories: `${API_BASE_URL}/histories`,
  reservationHistories: `${API_BASE_URL}/histories`,
  resourceHistories: `${API_BASE_URL}/resource-histories`,
  historyUpdates: `${API_BASE_URL}/history-updates`
} as const;

// ドラッグ操作の閾値（ピクセル）
export const DRAG_THRESHOLD = 3;

// クリック判定の閾値（ピクセル）
export const CLICK_THRESHOLD = 5;

// クリック判定の時間閾値（ミリ秒）
export const CLICK_TIME_THRESHOLD = 300;

// 同期サービス設定
export const SYNC_INTERVAL_MS = 30 * 1000; // 30秒ごとに同期 (ユーザー要望)
export const SYNC_PAST_DAYS = 14;          // 同期対象: 2週間前から
export const SYNC_FUTURE_DAYS = 365;       // 同期対象: 1年後まで
